
import u from "umbrellajs"
import Promise from "plugins/bluebird/index"

import AssetsInfoStore from "app/assets_info_store"

import LeafletSettings from "app/map/leaflet/settings"
import AppMapZoomLevels from "app/map/zoom_levels"

import DomIdGenerator from "app/utils/dom_id_generator"

import Geometry_Coordinates_ValueFromWgs84ToGsj02Conversion_Op from "app/geometry/coordinates/value_from_wgs84_to_gsj02_conversion/operation"

import ActiveSwitches from "./mark-switch-active"


interface Selector {
  scope:  string
  maps:   string
}

interface InputSelector {
  scope?:  string
  maps?:   string
}

const default_selector = {
  scope: ".cell--listings--pictures-and-map-browser--tabbed",
  maps:  ".js-google-map",
}


const MAP_INITIALIZED_CLASS_NAME = "cell--listings--pictures-and-map-browser--tabbed__google-map--js-spacious-initialized"


interface MapEntry {
  map:            L.Map,
  center:         L.LatLng,
  map_tile_layer: L.TileLayer & {
    _update?: () => void
  },
}


class GoogleMapDiv {
  private selector: Selector

  private $scope: UmbrellaInstance
  private $maps: UmbrellaInstance

  private maplist: MapEntry[]

  constructor(selector?: InputSelector) {
    this.selector = Object.assign({}, default_selector, selector)
    this.$scope = u(this.selector.scope)
    this.$maps = this.$scope.find(this.selector.maps)

    this.maplist = []
  }

  public create_with_promise(): Promise<void> {
    return this.created().then(() => {
      this.bind_action()
    })
  }

  private created(){
    return Promise.all(this.$maps.nodes.map((map_el) => {
      if (map_el.classList.contains(MAP_INITIALIZED_CLASS_NAME)) { return }
      map_el.classList.add(MAP_INITIALIZED_CLASS_NAME)

      // If we add map tile layer AFTER creating map object
      // The location of tile layer is drifted on load
      // To workaround this we create tile layer first and
      // pass it on map object creation
      return import(
        "plugins/leaflet"
      )
      .then(({L}) => {
        return import(
          "app/map/leaflet/map_tile_layer"
        )
        .then(({LeafletMapTileLayer}) => {
          return LeafletMapTileLayer.getNewTileLayerPromise()
          .then((map_tile_layer) => {
            return AssetsInfoStore.fetch("map/markers/s-spacious.png")
            .then((icon_image_data) => {
              const lat_str = map_el.getAttribute("data-lat")
              const lng_str = map_el.getAttribute("data-lng")
              if (lat_str == null || lng_str == null) {
                return
              }

              const lat = parseFloat(lat_str)
              const lng = parseFloat(lng_str)

              let center: L.LatLng = L.latLng(lat, lng)
              if (LeafletSettings.get_use_baidu_maps()) {
                const baidu_latlng = (new Geometry_Coordinates_ValueFromWgs84ToGsj02Conversion_Op({
                  latitude:   lat,
                  longitude:  lng,
                })).call()

                center = L.latLng(baidu_latlng.latitude, baidu_latlng.longitude)
              }

              const dom_id = DomIdGenerator.generateNewId()
              // Set new ID if applicable
              if (dom_id !== map_el.id) {
                map_el.setAttribute("id", dom_id)
              }

              const marker_icon = L.icon({
                iconUrl:  icon_image_data.url,
                iconSize: [25, 29],
              })

              const leaflet_map = LeafletSettings.get_use_baidu_maps() ?
                L.map(dom_id, {
                  center:       center,
                  zoom:         17,
                  minZoom:      AppMapZoomLevels.min,
                  maxZoom:      AppMapZoomLevels.max,
                  zoomControl:  true,

                  wheelPxPerZoomLevel: 210,

                  layers: [
                    map_tile_layer,
                  ],
                })
                :
                L.map(dom_id, {
                  center: center,

                  zoom:         17,
                  minZoom:      AppMapZoomLevels.min,
                  maxZoom:      AppMapZoomLevels.max,
                  zoomControl:  true,

                  // Has to be true, or labels doesn't follow panning
                  markerZoomAnimation:  true,
                  zoomAnimation:        false,
                  inertia:              false,

                  wheelPxPerZoomLevel: 210,

                  layers: [
                    map_tile_layer,
                  ],
                })

              L.marker(center, {
                icon: marker_icon,
              }).addTo(leaflet_map)
              this.maplist.push({map: leaflet_map, center, map_tile_layer})
            })
          })
        })
      })
    }))
  }
  private bind_action(){
    ActiveSwitches.instance().on("switch_click", () => {
      this.resize()
    })

    return this
  }
  private resize(){
    // setTimeout to makesure it is the last event handler
    // damn components/widgets/switch_mode
    setTimeout(() => {
      this.maplist.forEach((obj) => {
        const {map, center, map_tile_layer} = obj
        map.setView(center, 17, {
          animate:          false,
        })
        map.invalidateSize({
          animate:         false,
          debounceMoveend: true,
        })
        if (typeof map_tile_layer._update === "function") {
          map_tile_layer._update()
        }
      })
    }, 0)
  }
}


const factory = {
  create_with_promise(): Promise<void> {
    return new Promise((resolve) => {
      const x = new GoogleMapDiv()
      x.create_with_promise().then(() => {
        resolve()
      })
      .done()
    })
  },
}

export default factory
